'use client';

import React from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import { Icon } from '@liftango/ui-icons';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  label: string;
  disabled?: boolean;
}

const Wrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  color: ${(p) => p.theme.colors.neutral.ink};
  cursor: pointer;
  &[data-disabled] {
    opacity: 0.5;
    cursor: default;
  }
`;

const Box = styled(RadixCheckbox.Root)`
  width: 18px;
  height: 18px;
  border: 1px solid ${(p) => p.theme.colors.neutral.lineStrong};
  border-radius: ${(p) => p.theme.radius.sm};
  background: ${(p) => p.theme.colors.neutral.panel};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  &[data-state='checked'],
  &[data-state='indeterminate'] {
    background: ${(p) => p.theme.colors.brand.primary};
    border-color: ${(p) => p.theme.colors.brand.primary};
  }
  ${focusRing}
`;

export function Checkbox({ checked, onChange, indeterminate, label, disabled }: CheckboxProps) {
  const state = indeterminate ? 'indeterminate' : checked;
  return (
    <Wrapper data-disabled={disabled || undefined}>
      <Box
        checked={state}
        onCheckedChange={(v) => onChange(v === true)}
        disabled={disabled}
        aria-label={label}
      >
        <RadixCheckbox.Indicator>
          {indeterminate ? (
            <Icon d="M4 12h16" size={12} fill="none" stroke="#fff" strokeWidth={2} />
          ) : (
            <Icon name="check" size={12} fill="#fff" />
          )}
        </RadixCheckbox.Indicator>
      </Box>
      {label}
    </Wrapper>
  );
}
